// Admin → Activation: the Custom Rankings funnels.
//   creation:     editor opened → ranking started → ranking saved
//   acquisition:  ranking saved → shared → visitor viewed → Make Your Own → visitor saved → visitor registered
// Counts are distinct people (user or anonymous visitor) in the selected range.
import { useEffect, useState } from 'react';
import adminApi from '../../services/adminApi';
import styles from './OnboardingInsights.module.css';
import funnelStyles from './ActivationFunnel.module.css';

const pct = (value) => (value == null ? '—' : `${Math.round(value * 100)}%`);

function StepTable({ steps }) {
    const max = Math.max(1, ...steps.map((s) => s.count));
    return (
        <table className={funnelStyles.table}>
            <thead>
                <tr>
                    <th>Step</th>
                    <th className={funnelStyles.num}>People</th>
                    <th className={funnelStyles.num}>Step conv.</th>
                    <th className={funnelStyles.barCol} aria-hidden="true" />
                </tr>
            </thead>
            <tbody>
                {steps.map((step) => (
                    <tr key={step.key}>
                        <td>{step.label}</td>
                        <td className={funnelStyles.num}>{step.count.toLocaleString()}</td>
                        <td className={funnelStyles.num}>{pct(step.conversionFromPrevious)}</td>
                        <td className={funnelStyles.barCol}><div className={funnelStyles.bar} style={{ width: `${Math.round((step.count / max) * 100)}%` }} /></td>
                    </tr>
                ))}
            </tbody>
        </table>
    );
}

export default function RankingsFunnel({ range }) {
    const [data, setData] = useState(null);
    const [error, setError] = useState(null);

    useEffect(() => {
        let cancelled = false;
        setError(null);
        adminApi.getRankingFunnel({ range })
            .then((res) => { if (!cancelled) setData(res); })
            .catch((err) => { if (!cancelled) setError(err.message || 'Failed to load Custom Rankings stats'); });
        return () => { cancelled = true; };
    }, [range]);

    if (error) return <section className={styles.section}><h2 className={styles.sectionTitle}>Custom Rankings</h2><p>{error}</p></section>;
    if (!data) return null;
    const { totals } = data;

    return (
        <section className={styles.section}>
            <h2 className={styles.sectionTitle}>Custom Rankings — creation funnel</h2>
            <StepTable steps={data.creationSteps} />
            <p className={funnelStyles.notes} style={{ marginTop: 12 }}>
                Sign-up gate shown to {totals.signupGateShown.toLocaleString()} guests · {totals.signupsThroughRankings.toLocaleString()} new accounts came through rankings.
            </p>
            <h2 className={styles.sectionTitle}>Custom Rankings — acquisition funnel</h2>
            <StepTable steps={data.steps} />
            <p className={funnelStyles.notes} style={{ marginTop: 12 }}>
                {totals.rankingsCreated} rankings saved ({totals.publicRankings} public) · {totals.referredSignups} referred signups,{' '}
                {totals.referredSignupsWithRanking} of whom saved their own ranking.
            </p>
            <table className={`${funnelStyles.table} ${funnelStyles.compact}`}>
                <thead>
                    <tr><th>Event</th><th className={funnelStyles.num}>Total</th><th className={funnelStyles.num}>Unique</th></tr>
                </thead>
                <tbody>
                    {data.events.map((e) => (
                        <tr key={e.eventType}><td>{e.eventType}</td><td className={funnelStyles.num}>{e.total.toLocaleString()}</td><td className={funnelStyles.num}>{e.uniqueActors.toLocaleString()}</td></tr>
                    ))}
                </tbody>
            </table>
            <ul className={funnelStyles.notes}>
                {data.notes.map((n) => <li key={n}>{n}</li>)}
            </ul>
        </section>
    );
}
